import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { formatDuration } from '@turn-connect/utils';
import { DIFF_BREAK_COLOR, DIFF_WORK_COLOR, buildCorrectionDiff } from './CorrectionDiff.types';
/**
 * CorrectionDiff - Web
 *
 * Shows a correction request as before and after: two bars on one shared time
 * axis, then every field that moves, old value to new. The approver decides
 * from this, so it renders the STORED snapshot rather than the live entry —
 * the live one drifts and is gone once the change is applied.
 */
const fmtTime = (iso, timezone) => {
    if (!iso)
        return '—';
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        ...(timezone ? { timeZone: timezone } : {}),
    });
};
const signedDuration = (minutes) => `${minutes > 0 ? '+' : minutes < 0 ? '−' : ''}${formatDuration(Math.abs(minutes), { round: 'round' })}`;
const Bar = ({ bar, dimmed }) => (_jsx("div", { style: {
        display: 'flex',
        height: 30,
        background: '#111827',
        borderRadius: 8,
        overflow: 'hidden',
    }, children: bar.segments.map((seg) => (_jsx("div", { title: seg.kind === 'empty' ? undefined : `${seg.label} · ${formatDuration(seg.durationMinutes, { round: 'round' })}`, style: {
            width: `${seg.widthPct}%`,
            background: seg.kind === 'work' ? DIFF_WORK_COLOR : seg.kind === 'break' ? DIFF_BREAK_COLOR : 'transparent',
            opacity: dimmed && seg.kind !== 'empty' ? 0.55 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 600,
            color: '#FFFFFF',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        }, children: seg.widthPct > 14 && seg.kind !== 'empty' ? formatDuration(seg.durationMinutes, { round: 'round' }) : '' }, seg.key))) }));
export const CorrectionDiff = ({ snapshot, proposal, timezone, compact = false, testID = 'correction-diff', }) => {
    const model = useMemo(() => buildCorrectionDiff(snapshot, proposal), [snapshot, proposal]);
    const { impact, changes } = model;
    return (_jsxs("div", { "data-testid": testID, style: { display: 'flex', flexDirection: 'column', gap: 13 }, children: [!compact && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 5 }, children: [_jsxs("div", { style: labelRow, children: [_jsx("span", { style: { ...pill, background: 'rgba(107,114,128,0.3)', color: '#9CA3AF' }, children: "BEFORE" }), _jsxs("span", { children: ["as recorded \u00B7 ", _jsx("span", { style: mono, children: formatDuration(model.before.netMinutes, { round: 'round' }) }), " net"] })] }), _jsx(Bar, { bar: model.before, dimmed: true })] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 5 }, children: [_jsxs("div", { style: labelRow, children: [_jsx("span", { style: { ...pill, background: 'rgba(59,130,246,0.22)', color: '#93BDFB' }, children: "AFTER" }), _jsxs("span", { children: ["as proposed \u00B7", ' ', _jsx("span", { style: { ...mono, color: impact.deltaMinutes >= 0 ? DIFF_WORK_COLOR : '#EF4444' }, children: formatDuration(model.after.netMinutes, { round: 'round' }) }), ' ', "net"] })] }), _jsx(Bar, { bar: model.after }), _jsxs("div", { style: axisRow, children: [_jsx("span", { children: fmtTime(model.axisStart, timezone) }), _jsx("span", { children: fmtTime(model.axisMid, timezone) }), _jsx("span", { children: fmtTime(model.axisEnd, timezone) })] })] })] })), _jsxs("div", { style: changeBox, children: [_jsxs("h4", { style: changeTitle, children: [changes.length, " ", changes.length === 1 ? 'change' : 'changes'] }), changes.map((c) => (_jsxs("div", { style: changeRow, children: [_jsx("span", { style: { color: '#9CA3AF', minWidth: 116 }, children: c.label }), c.oldValue && _jsx("span", { style: oldVal, children: c.isText ? c.oldValue : fmtTime(c.oldValue, timezone) }), c.oldValue && c.newValue && _jsx("span", { style: { color: '#6B7280' }, children: "\u2192" }), c.newValue && _jsx("span", { style: newVal, children: c.isText ? c.newValue : fmtTime(c.newValue, timezone) })] }, c.key))), _jsxs("div", { style: { ...changeRow, borderTop: '1px solid rgba(59,130,246,0.28)', paddingTop: 7, marginTop: 2 }, children: [_jsx("span", { style: { color: '#E5E7EB', minWidth: 116 }, children: "Net worked" }), _jsx("span", { style: oldVal, children: formatDuration(impact.beforeMinutes, { round: 'round' }) }), _jsx("span", { style: { color: '#6B7280' }, children: "\u2192" }), _jsx("span", { style: newVal, children: formatDuration(impact.afterMinutes, { round: 'round' }) }), _jsx("span", { style: {
                                    ...mono,
                                    fontSize: 11.5,
                                    color: impact.deltaMinutes > 0 ? DIFF_WORK_COLOR : impact.deltaMinutes < 0 ? '#EF4444' : '#9CA3AF',
                                }, children: signedDuration(impact.deltaMinutes) })] })] })] }));
};
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const labelRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 11.5,
    color: '#9CA3AF',
};
const pill = {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 999,
    letterSpacing: '0.04em',
};
const axisRow = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 10.5,
    color: '#6B7280',
    marginTop: 3,
    ...mono,
};
const changeBox = {
    background: 'rgba(59,130,246,0.09)',
    border: '1px solid rgba(59,130,246,0.34)',
    borderRadius: 9,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
};
const changeTitle = {
    fontSize: 11,
    fontWeight: 600,
    color: '#93BDFB',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    margin: 0,
};
const changeRow = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 12,
};
const oldVal = { ...mono, color: '#6B7280', textDecoration: 'line-through' };
const newVal = { ...mono, color: '#FFFFFF', fontWeight: 600 };
export default CorrectionDiff;
